import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { StatusBadge } from './StatusBadge';
import {
  FileText, DollarSign, Clock, AlertCircle, MessageCircle, Send,
  ExternalLink, Building2, User, CheckCircle, Loader2
} from 'lucide-react';
import DocumentUploader from './DocumentUploader';
import BorrowerCompletionFlow from './BorrowerCompletionFlow';
import axios from 'axios';

const linkifyContent = (text) => {
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer"
        className="underline font-medium text-teal-600 hover:text-teal-500 break-all">{part}</a>
    ) : part
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FAMILY_OFFICE_SUBTYPES = [
  { value: 'term_loans',                label: 'Term Loans',              desc: 'Fixed-rate financing with predictable monthly payments' },
  { value: 'lines_of_credit',           label: 'Lines of Credit',         desc: 'Flexible revolving capital — pay interest on what you use' },
  { value: 'non_collateralized_loans',  label: 'Non-Collateralized Loans', desc: 'Unsecured financing based on business performance' },
  { value: 'asset_based_lending',       label: 'Asset-Based Lending',     desc: 'Loan secured by AR, inventory, equipment, or real estate' },
  { value: 'equipment_financing',       label: 'Equipment Financing',     desc: 'Acquire or lease equipment while preserving cash flow' },
];

const FamilyOfficeSubtypePicker = ({ token, selected, onSaved }) => {
  const [choices, setChoices] = useState(selected || []);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => { setChoices(selected || []); }, [selected]);

  const toggle = (val) => {
    setChoices(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
    setSaveMessage('');
  };

  const handleSave = async () => {
    if (choices.length === 0) {
      setSaveMessage('Please select at least one sub-program.');
      return;
    }
    setSaving(true);
    setSaveMessage('');
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/public/borrower-family-office-subtypes/${token}`,
        { form_data: { family_office_data: { selected_subtypes: choices } } }
      );
      setSaveMessage(`Saved — ${res.data.required_documents_count} document(s) requested below.`);
      if (onSaved) await onSaved();
    } catch (err) {
      setSaveMessage(err.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify([...choices].sort()) !== JSON.stringify([...(selected || [])].sort());
  const isEmpty = (selected || []).length === 0;

  return (
    <Card className={isEmpty ? 'border-violet-300 shadow-md' : ''} data-testid="family-office-subtype-picker">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 text-violet-800">
          <Building2 className="h-4 w-4" />
          Family Office Programs — Sub-Programs
          {isEmpty && <Badge className="bg-violet-100 text-violet-700 border border-violet-300 ml-auto">Action Needed</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-3">
          {isEmpty
            ? 'Select every program you are interested in. Your document requirements below will update based on your selection — you can pick more than one.'
            : 'You can change which sub-programs apply. Your document checklist will update to match.'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {FAMILY_OFFICE_SUBTYPES.map(st => {
            const checked = choices.includes(st.value);
            return (
              <label
                key={st.value}
                className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  checked ? 'border-violet-400 bg-violet-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
                data-testid={`subtype-option-${st.value}`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={checked}
                  onChange={() => toggle(st.value)}
                />
                <div>
                  <div className="text-sm font-medium text-slate-800">{st.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{st.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-slate-500">{choices.length} selected</div>
          <Button
            size="sm"
            className="bg-violet-700 hover:bg-violet-800 text-white"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            data-testid="save-subtypes-btn"
          >
            {saving ? 'Saving...' : hasChanges ? 'Save & Update Document Checklist' : 'Saved'}
          </Button>
        </div>
        {saveMessage && (
          <p className={`mt-2 text-xs ${saveMessage.startsWith('Saved') ? 'text-emerald-700' : 'text-red-600'}`}>{saveMessage}</p>
        )}
      </CardContent>
    </Card>
  );
};

const BorrowerMessages = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/messages/${token}`);
      setMessages(res.data.messages || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/messages/${token}`, { content: newMsg.trim() });
      setNewMsg('');
      await fetchMessages();
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  return (
    <Card data-testid="borrower-messages">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <MessageCircle className="h-4 w-4" />
          Messages
          {messages.length > 0 && <Badge variant="secondary" className="ml-auto">{messages.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden">
          <div className="h-64 overflow-y-auto p-3 space-y-3 bg-slate-50">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : messages.length === 0 ? (
              <p className="text-center text-sm text-slate-400 mt-8">No messages yet. Send a message to your loan team.</p>
            ) : (
              messages.map(msg => {
                const isBorrower = msg.sender_role === 'borrower';
                return (
                  <div key={msg.id} className={`flex ${isBorrower ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 ${isBorrower ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-800'}`}>
                      {!isBorrower && <p className="text-xs font-medium text-slate-500 mb-0.5">{msg.sender_name} ({msg.sender_role})</p>}
                      <p className="text-sm whitespace-pre-line">{linkifyContent(msg.content)}</p>
                      <p className={`text-xs mt-1 ${isBorrower ? 'text-slate-300' : 'text-slate-400'}`}>
                        {new Date(msg.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>
          <div className="flex items-end gap-2 p-2 border-t bg-white">
            <textarea
              value={newMsg}
              onChange={e => {
                setNewMsg(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (newMsg.trim() && !sending) handleSend();
                }
              }}
              placeholder="Type a message..."
              className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#66C2CA]/40 focus:border-transparent"
              disabled={sending}
              rows={1}
              style={{ minHeight: '38px', maxHeight: '120px' }}
              data-testid="borrower-message-input"
            />
            <Button size="sm" onClick={handleSend} disabled={!newMsg.trim() || sending} className="bg-[#081120] hover:bg-[#0E333F]" data-testid="borrower-send-btn">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const STATUS_STEPS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'under_review', label: 'Under Review' },
  { key: 'pending_docs', label: 'Pending Docs/Info' },
  { key: 'approved', label: 'Approved' },
  { key: 'closing', label: 'Closing' },
  { key: 'closed', label: 'Closed' },
  { key: 'funded', label: 'Funded' },
];

const STATUS_ORDER = {
  submitted: 0,
  under_review: 1,
  pending_docs: 2,
  approved: 3,
  closing: 4,
  closed: 5,
  funded: 6,
  declined: -1,
};

const BorrowerStatusPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/borrower-status/${token}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? 'This link is invalid or has expired.' : 'Unable to load application status.');
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, [token]);

  const refetchStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/borrower-status/${token}`);
      setData(res.data);
    } catch (err) { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-slate-500 mx-auto mb-3" />
          <p className="text-slate-600">Loading application status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Link Not Found</h2>
            <p className="text-slate-600 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isDeclined = data.status === 'declined';
  const isIncomplete = data.status === 'incomplete';
  const hasRequestedInfo = (data.requested_fields?.length > 0) || (data.custom_info_requests?.length > 0);
  const currentStep = STATUS_ORDER[data.status] ?? 0;

  const handleCompletionDone = () => {
    // Refetch status to show the submitted view
    window.location.reload();
  };

  // Show the completion flow when:
  //  • Application is still being drafted (incomplete), OR
  //  • The underwriter has explicitly requested fields, OR
  //  • The broker/UW shared this link to let the borrower fill in any
  //    missing information on an already-submitted application.
  const allowCompletion = data.allow_completion;
  if (isIncomplete || hasRequestedInfo || allowCompletion) {
    const headerText = hasRequestedInfo && !isIncomplete
      ? 'Information Requested'
      : isIncomplete
        ? 'Complete Your Application'
        : 'Review & Complete Your Application';
    const subText = hasRequestedInfo && !isIncomplete
      ? 'The underwriter has requested additional information for your application.'
      : isIncomplete
        ? 'Your broker has started an application for you. Please provide the remaining information below.'
        : 'Your broker has invited you to review your application. Please fill in any missing details below — anything already entered is shown for your reference.';
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="borrower-status-page">
        <div className="bg-[#081120] text-white">
          <div className="max-w-3xl mx-auto px-4 py-6 sm:px-6">
            <div className="flex items-center gap-3 mb-1">
              <FileText className="h-6 w-6 text-slate-300" />
              <h1 className="text-xl sm:text-2xl font-bold">{headerText}</h1>
            </div>
            <p className="text-slate-300 text-sm">{subText}</p>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6 space-y-6">
          {/* Broker Contact */}
          {data.broker && (
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{data.broker.name}</p>
                    {data.broker.company_name && <p className="text-xs text-slate-500">{data.broker.company_name}</p>}
                    {data.broker.email && <a href={`mailto:${data.broker.email}`} className="text-xs text-blue-600 hover:underline">{data.broker.email}</a>}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Completion Flow */}
          <BorrowerCompletionFlow
            token={token}
            initialData={data.form_data || {}}
            loanTypes={data.loan_types || [data.product_type]}
            onComplete={handleCompletionDone}
            requestedFields={data.requested_fields || []}
            customInfoRequests={data.custom_info_requests || []}
            applicationStatus={data.status}
            allowCompletion={!!data.allow_completion}
            productType={data.product_type}
            useOfFundsSchedule={data.use_of_funds_schedule || []}
            amountRequested={data.amount_requested || 0}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="borrower-status-page">
      {/* Header */}
      <div className="bg-[#081120] text-white">
        <div className="max-w-3xl mx-auto px-4 py-6 sm:px-6">
          <div className="flex items-center gap-3 mb-1">
            <FileText className="h-6 w-6 text-slate-300" />
            <h1 className="text-xl sm:text-2xl font-bold">Application Status</h1>
          </div>
          <p className="text-slate-300 text-sm">Track the progress of your loan application</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 sm:px-6 space-y-6">
        {/* Business & Loan Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800" data-testid="borrower-biz-name">{data.business_name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline">{(data.product_type || '').replace('_', ' ').toUpperCase()}</Badge>
                  <StatusBadge status={data.status} />
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Requested Amount</p>
                <p className="text-2xl font-bold text-green-600" data-testid="borrower-amount">
                  ${(data.amount_requested || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Progress Tracker */}
        {!isDeclined ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {STATUS_STEPS.map((step, i) => {
                  const isComplete = i <= currentStep;
                  const isCurrent = i === currentStep;
                  return (
                    <div key={step.key} className="flex items-start gap-4 mb-0 last:mb-0" data-testid={`progress-step-${step.key}`}>
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                          isCurrent
                            ? 'bg-slate-800 border-slate-800 text-white ring-4 ring-slate-200'
                            : isComplete
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'bg-white border-slate-300 text-slate-400'
                        }`}>
                          {isComplete && !isCurrent ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <span className="text-xs font-bold">{i + 1}</span>
                          )}
                        </div>
                        {i < STATUS_STEPS.length - 1 && (
                          <div className={`w-0.5 h-8 ${isComplete && !isCurrent ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                        )}
                      </div>
                      <div className={`pt-1 ${isCurrent ? 'font-semibold text-slate-800' : isComplete ? 'text-emerald-700' : 'text-slate-400'}`}>
                        <p className="text-sm">{step.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-red-800 mb-1">Application Declined</h3>
              <p className="text-sm text-red-600">Please contact your broker for more information.</p>
            </CardContent>
          </Card>
        )}

        {/* Family Office — Sub-Program picker */}
        {data.product_type === 'family_office' && !isDeclined && (
          <FamilyOfficeSubtypePicker
            token={token}
            selected={data.family_office_selected_subtypes || []}
            onSaved={() => refetchStatus()}
          />
        )}

        {/* Document Upload Section */}
        {(data.required_documents || []).length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <DocumentUploader
                applicationId={data.application_id}
                token={token}
                requiredDocuments={data.required_documents}
                onUploadComplete={() => refetchStatus()}
              />
            </CardContent>
          </Card>
        )}

        {/* Missing Documents (legacy — shown when no required_documents) */}
        {(!data.required_documents || data.required_documents.length === 0) && data.missing_documents && data.missing_documents.length > 0 && (
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2 text-orange-700">
                <AlertCircle className="h-4 w-4" />
                Documents Needed
                <Badge className="bg-orange-100 text-orange-700 border border-orange-300 ml-auto">{data.missing_documents.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3">The following documents are required to continue processing your application:</p>
              <div className="space-y-2" data-testid="borrower-missing-docs">
                {data.missing_documents.map((doc, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-md text-sm text-orange-800">
                    <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                    {doc}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Link */}
        {data.document_link && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-slate-600">Document folder:</span>
                <a
                  href={data.document_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate"
                  data-testid="borrower-doc-link"
                >
                  {data.document_link}
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Messages */}
        <BorrowerMessages token={token} />

        {/* Broker Contact */}
        {data.broker && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" />
                Your Broker
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="font-medium text-slate-800" data-testid="borrower-broker-name">{data.broker.name}</p>
                {data.broker.company_name && (
                  <p className="text-sm text-slate-500">{data.broker.company_name}</p>
                )}
                {data.broker.email && (
                  <a href={`mailto:${data.broker.email}`} className="text-sm text-blue-600 hover:underline">
                    {data.broker.email}
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dates */}
        <div className="text-center text-xs text-slate-400 pb-4">
          Applied {data.created_at ? new Date(data.created_at).toLocaleDateString() : 'N/A'}
          {data.updated_at && ` · Last updated ${new Date(data.updated_at).toLocaleDateString()}`}
        </div>
      </div>
    </div>
  );
};

export default BorrowerStatusPage;
